interface StreamingContent {
  id: string;
  title: string;
  type: 'movie' | 'series' | 'music';
  platform: string;
  genre: string[];
  mood?: string[];
  rating?: number;
}

interface PlaylistData {
  id: string;
  name: string;
  tracks: string[];
  mood: string[];
  context?: string;
}

export class StreamingService {
  private static instance: StreamingService;
  private spotifyToken: string;
  private netflixToken: string;

  private constructor() {
    this.spotifyToken = import.meta.env.VITE_SPOTIFY_TOKEN || '';
    this.netflixToken = import.meta.env.VITE_NETFLIX_TOKEN || '';
  }

  static getInstance(): StreamingService {
    if (!StreamingService.instance) {
      StreamingService.instance = new StreamingService();
    }
    return StreamingService.instance;
  }

  async getRecommendations(mood: string, activity?: string): Promise<StreamingContent[]> {
    // Combina recomendações de diferentes plataformas
    const [musicRecs, videoRecs] = await Promise.all([
      this.getMusicRecommendations(mood, activity),
      this.getVideoRecommendations(mood)
    ]);

    return [...musicRecs, ...videoRecs];
  }

  async createContextualPlaylist(mood: string, activity: string): Promise<PlaylistData | null> {
    try {
      const response = await fetch('https://api.spotify.com/v1/recommendations', {
        headers: {
          'Authorization': `Bearer ${this.spotifyToken}`
        },
        params: {
          seed_genres: this.getMoodGenres(mood),
          target_energy: this.getMoodEnergy(mood),
          target_valence: this.getMoodValence(mood),
          limit: 20
        }
      });

      const data = await response.json();
      return {
        id: Date.now().toString(),
        name: `${mood} ${activity} Mix`,
        tracks: data.tracks.map((t: any) => t.id),
        mood: [mood],
        context: activity
      };
    } catch (error) {
      console.error('Erro ao criar playlist:', error);
      return null;
    }
  }

  private getMoodGenres(mood: string): string[] {
    const moodGenres: Record<string, string[]> = {
      'feliz': ['pop', 'dance', 'happy'],
      'focado': ['ambient', 'classical', 'focus'],
      'energético': ['rock', 'electronic', 'workout'],
      'relaxado': ['chill', 'jazz', 'acoustic'],
      'romântico': ['romance', 'r-n-b', 'love']
    };

    return moodGenres[mood.toLowerCase()] || ['pop'];
  }

  private getMoodEnergy(mood: string): number {
    const energyLevels: Record<string, number> = {
      'feliz': 0.8,
      'focado': 0.5,
      'energético': 0.9,
      'relaxado': 0.3,
      'romântico': 0.6
    };

    return energyLevels[mood.toLowerCase()] || 0.6;
  }

  private getMoodValence(mood: string): number {
    const valenceLevels: Record<string, number> = {
      'feliz': 0.8,
      'focado': 0.6,
      'energético': 0.7,
      'relaxado': 0.5,
      'romântico': 0.6
    };

    return valenceLevels[mood.toLowerCase()] || 0.6;
  }

  private async getMusicRecommendations(mood: string, activity?: string): Promise<StreamingContent[]> {
    try {
      const response = await fetch('https://api.spotify.com/v1/recommendations', {
        headers: {
          'Authorization': `Bearer ${this.spotifyToken}`
        },
        params: {
          seed_genres: this.getMoodGenres(mood),
          limit: 10
        }
      });

      const data = await response.json();
      return data.tracks.map((track: any) => ({
        id: track.id,
        title: track.name,
        type: 'music',
        platform: 'Spotify',
        genre: track.genres || [],
        mood: [mood],
        rating: track.popularity / 100
      }));
    } catch {
      return [];
    }
  }

  private async getVideoRecommendations(mood: string): Promise<StreamingContent[]> {
    // Simulação de recomendações da Netflix
    // Em produção, usar API real da Netflix
    const mockRecommendations: StreamingContent[] = [
      {
        id: '1',
        title: 'Filme Feliz',
        type: 'movie',
        platform: 'Netflix',
        genre: ['Comédia'],
        mood: ['feliz'],
        rating: 0.85
      },
      {
        id: '2',
        title: 'Série Relaxante',
        type: 'series',
        platform: 'Netflix',
        genre: ['Drama'],
        mood: ['relaxado'],
        rating: 0.9
      }
    ];

    return mockRecommendations.filter(rec => rec.mood?.includes(mood));
  }

  async controlPlayback(command: string, platform: string): Promise<string> {
    // Implementar controle de reprodução para cada plataforma
    switch (platform.toLowerCase()) {
      case 'spotify':
        return this.controlSpotify(command);
      case 'netflix':
        return this.controlNetflix(command);
      default:
        return 'Plataforma não suportada';
    }
  }

  private async controlSpotify(command: string): Promise<string> {
    try {
      const endpoint = command === 'play' ? 'play' : 
                      command === 'pause' ? 'pause' : 
                      command === 'next' ? 'next' : 'previous';

      await fetch(`https://api.spotify.com/v1/me/player/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.spotifyToken}`
        }
      });

      return `Spotify: ${command}`;
    } catch {
      return 'Erro ao controlar Spotify';
    }
  }

  private async controlNetflix(command: string): Promise<string> {
    // Simular controle da Netflix
    return `Netflix: ${command} (simulado)`;
  }
}

export const streamingService = StreamingService.getInstance();
